import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ModalAddMachine from '../../components/ModalAddMachine';
import { Typography } from '@mui/material';
import styles from './style.module.css';
import TableComponent from '../../components/Table';
import { useMachines } from '../../hooks/useMachines';
import { fetchMachinesData } from '../../helpers/fetchData';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import CustomTabs from '../../components/CustomTabs';
import { requestSearch } from '../../helpers/search';
import { ACTIONS } from '../../constants/actionTables';

function MachinesList() {
  const [activeTab, setActiveTab] = useState('active');
  const [dataActive, setDataActive] = useState([]);
  const [dataAchived, setDataAchived] = useState([]);
  const [filteredDataActive, setFilteredDataActive] = useState([]);
  const [filteredDataArchived, setFilteredDataArchived] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const { user } = useAuth();
  const dispatch = useDispatch();
  const { machines } = useMachines();

  // Load machines data on mount - only once
  useEffect(() => {
    fetchMachinesData(dispatch);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const groupFilter = searchParams.get('group');

  useEffect(() => {
    if (machines?.activeMachines) {
      let data = machines.activeMachines.map((machine) => ({
        id: machine.id,
        number: machine.number,
        machine_details: {
          name: machine.name,
          description: machine.description,
          serial_number: machine.serial_number,
        },
        responsible_person: {
          responsibleName: machine.responsible_person
            ? `${machine.responsible_person.firstname} ${machine.responsible_person.lastname}`
            : '-',
          deputyName: machine.deputy
            ? `${machine.deputy.firstname} ${machine.deputy.lastname}`
            : '-',
        },
        year_of_manufacture: machine.year_of_manufacture,
        machine_group: machine.machine_group
          ? { id: machine.machine_group.id, name: machine.machine_group.name }
          : null,
        location: machine.location?.name || '-',
        status: machine.status,
      }));

      // Apply group filter if present
      if (groupFilter) {
        data = data.filter((machine) => machine.machine_group?.name === groupFilter);
      }

      setDataActive(data);
      setFilteredDataActive(data);
    }
    if (machines?.archivedMachines) {
      let data = machines.archivedMachines.map((machine) => ({
        id: machine.id,
        number: machine.number,
        machine_details: {
          name: machine.name,
          description: machine.description,
          serial_number: machine.serial_number,
        },
        responsible_person: {
          responsibleName: machine.responsible_person
            ? `${machine.responsible_person.firstname} ${machine.responsible_person.lastname}`
            : '-',
          deputyName: machine.deputy
            ? `${machine.deputy.firstname} ${machine.deputy.lastname}`
            : '-',
        },
        year_of_manufacture: machine.year_of_manufacture,
        machine_group: machine.machine_group
          ? { id: machine.machine_group.id, name: machine.machine_group.name }
          : null,
        location: machine.location?.name || '-',
        status: machine.status,
      }));

      // Apply group filter if present
      if (groupFilter) {
        data = data.filter((machine) => machine.machine_group?.name === groupFilter);
      }

      setDataAchived(data);
      setFilteredDataArchived(data);
    }
  }, [machines, groupFilter]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handleSearch = (searchValue) => {
    if (activeTab === 'active') {
      requestSearch(searchValue, dataActive || [], setFilteredDataActive);
    } else {
      requestSearch(searchValue, dataAchived || [], setFilteredDataArchived);
    }
  };

  const handleClearSearch = () => {
    if (groupFilter) {
      // If there's a group filter, navigate to clear it
      navigate('/machines');
    } else {
      // If there's no group filter, just clear the search and show all machines
      setFilteredDataActive(dataActive);
      setFilteredDataArchived(dataAchived);
    }
  };

  const getMachinesCount = () => {
    if (activeTab === 'active') {
      return filteredDataActive?.length || 0;
    } else {
      return filteredDataArchived?.length || 0;
    }
  };

  const handleSort = (columnKey) => {
    let direction = 'asc';
    let newSortConfig = { key: columnKey, direction };

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
        newSortConfig = { key: columnKey, direction };
      } else if (sortConfig.direction === 'desc') {
        // Reset sorting - show original data
        newSortConfig = { key: null, direction: 'asc' };
        setSortConfig(newSortConfig);

        if (activeTab === 'active') {
          setFilteredDataActive(dataActive);
        } else {
          setFilteredDataArchived(dataAchived);
        }
        return;
      }
    }

    setSortConfig(newSortConfig);

    const dataToSort = activeTab === 'active' ? [...filteredDataActive] : [...filteredDataArchived];
    const sortedData = dataToSort.sort((a, b) => {
      if (a[columnKey] < b[columnKey]) {
        return direction === 'asc' ? -1 : 1;
      }
      if (a[columnKey] > b[columnKey]) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    if (activeTab === 'active') {
      setFilteredDataActive(sortedData);
    } else {
      setFilteredDataArchived(sortedData);
    }
  };

  const handleRowClick = (machineId) => {
    navigate(`/machines/${machineId}`);
  };

  const titles = [
    t('generic.number'),
    t('generic.machineDetails'),
    t('generic.responsiblePerson'),
    t('generic.yearOfManufacture'),
    t('generic.machineGroups'),
    t('generic.location'),
    t('generic.status'),
    t('generic.action'),
  ];

  const tabs = [
    {
      key: 'active',
      label: t('machineList.activeMachines'),
      onClick: () => handleTabChange('active'),
    },
    {
      key: 'archived',
      label: t('machineList.archivedMachines'),
      onClick: () => handleTabChange('archived'),
    },
  ];

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.caption}>
          <Typography variant="h5" fontWeight="bold" className={styles.headerTitle}>
            {t('generic.machinesList')}
            {groupFilter && (
              <span style={{ color: '#3b82f6', fontSize: '0.8em', marginLeft: '8px' }}>
                - {t('generic.group')}: {groupFilter}
              </span>
            )}
          </Typography>
          <Typography className={styles.headerSubtitle}>{t('generic.machinesSubtitle')}</Typography>
        </div>
        {user?.role === 'admin' && <ModalAddMachine />}
      </div>

      <CustomTabs tabs={tabs} activeTab={activeTab} />

      <div className={styles.tabs}>
        {activeTab === 'active' && (
          <TableComponent
            titles={titles.slice(0, -1)}
            data={filteredDataActive}
            search={true}
            showTableHeader={true}
            tableTitle={t('generic.machinesList')}
            tableCount={`${getMachinesCount()} ${t('machineList.activeMachines')}`}
            searchPlaceholder={t('generic.search')}
            initialSearchValue={groupFilter}
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            onSort={handleSort}
            sortConfig={sortConfig}
            sortableColumns={[
              'number',
              'machine_details',
              'responsible_person',
              'year_of_manufacture',
              'machine_group',
              'location',
              'status',
            ]}
            columnKeys={[
              'number',
              'machine_details',
              'responsible_person',
              'year_of_manufacture',
              'machine_group',
              'location',
              'status',
            ]}
            onRowClick={handleRowClick}
          />
        )}
        {activeTab === 'archived' && (
          <TableComponent
            titles={titles}
            data={filteredDataArchived}
            action={ACTIONS.ARCHIVED_MACHINES}
            search={true}
            showTableHeader={true}
            tableTitle={t('generic.machinesList')}
            tableCount={`${getMachinesCount()} ${t('machineList.archivedMachines')}`}
            searchPlaceholder={t('generic.search')}
            initialSearchValue={groupFilter}
            onSearch={handleSearch}
            onClearSearch={handleClearSearch}
            onSort={handleSort}
            sortConfig={sortConfig}
            sortableColumns={[
              'number',
              'machine_details',
              'responsible_person',
              'year_of_manufacture',
              'machine_group',
              'location',
              'status',
            ]}
            columnKeys={[
              'number',
              'machine_details',
              'responsible_person',
              'year_of_manufacture',
              'machine_group',
              'location',
              'status',
            ]}
            onRowClick={handleRowClick}
          />
        )}
      </div>
    </section>
  );
}

export default MachinesList;
